/**
 * Resolve the source ent id for an event. FullCalendar's `event.id` is a stable
 * composite for recurring instances (so a series split doesn't remount tiles),
 * so the real source id is read from `extendedProps.gcalId`. Falls back to
 * `event.id` for non-recurring real events (id === gcalId) and for synthetic
 * events (selection ranges, proposed slots) that have no `gcalId`.
 */
export function sourceIdOf(event: { id: string; extendedProps: Record<string, unknown> }): string {
  return (event.extendedProps["gcalId"] as string | undefined) ?? event.id;
}

/**
 * The class naming *which* event a highlight targets, so a lookup can confirm
 * the chip it found is the one it asked for rather than trusting that only one
 * highlight is ever live (two callers set highlights now: the canvas's focused
 * event and notification deep links, and `CanvasCalendarBody` picks between them).
 *
 * FullCalendar's only per-event styling channel is `classNames`, and an
 * `instanceKey` is an opaque provider string that can hold anything, so it's
 * hashed (djb2) into a class-safe token rather than escaped. A collision would
 * need two *simultaneously stamped* keys to hash alike, and at most one key is
 * stamped at a time.
 */
export function highlightTargetClass(instanceKey: string): string {
  let hash = 5381;
  for (let i = 0; i < instanceKey.length; i++) {
    hash = (hash * 33 + instanceKey.charCodeAt(i)) | 0;
  }
  return `is-highlight-${(hash >>> 0).toString(36)}`;
}

/**
 * Stamp `is-highlighted` on the event matching the source the agent referenced,
 * so the CSS rings it, plus {@link highlightTargetClass} so a chip lookup can
 * verify which event it found. Matches on the server-resolved `instanceKey`,
 * which already accounts for the agenda's deduplication. Appends to any existing
 * `classNames` (e.g. a selection range's) and spreads the matched event so the
 * caller's memoized source array is never mutated.
 */
export function stampHighlightedEvent<
  E extends {
    extendedProps: Record<string, unknown>;
    classNames?: string[];
  },
>(events: E[], instanceKey: string | undefined): (E & { classNames?: string[] })[] {
  if (!instanceKey) return events;
  // Preserve array identity when nothing matches. The caller stamps all four
  // calendars and the target is in at most one, so mapping regardless would hand
  // the other three a fresh array and rebuild their tiles for nothing.
  if (!events.some((e) => e.extendedProps["instanceKey"] === instanceKey)) return events;
  return events.map((e) =>
    e.extendedProps["instanceKey"] === instanceKey
      ? {
          ...e,
          classNames: [
            ...(e.classNames ?? []),
            "is-highlighted",
            highlightTargetClass(instanceKey),
          ],
        }
      : e,
  );
}
